import { useEffect, useMemo, useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { AdminBarChart } from '@/components/admin/bar-chart';
import { AdminButton } from '@/components/admin/button';
import { Card, CardBody, CardHead } from '@/components/admin/card';
import { AdminIcon } from '@/components/admin/icon';
import { AdminLineChart } from '@/components/admin/line-chart';
import { PageHead } from '@/components/admin/page-head';
import { Segmented } from '@/components/admin/segmented';
import { Tag } from '@/components/admin/tag';
import { usePermissions } from '@/hooks/use-permissions';
import { fmtFCFA } from '@/lib/format';
import {
  reportingBusinessApi,
  type DirectionReport,
  type FinanceReport,
  type LogisticsReport,
  type SupportReport,
} from '@/lib/reporting/business';
import { monthlySummaryApi } from '@/lib/reporting/monthly-summary';

type ViewKey = 'direction' | 'finance' | 'logistics' | 'support';

const VIEW_PERMISSIONS: Record<ViewKey, string> = {
  direction: 'REPORTING.DIRECTION.READ',
  finance: 'FINANCE.READ',
  logistics: 'LOGISTICS.READ',
  support: 'SUPPORT.READ',
};

const VIEW_LABEL: Record<ViewKey, string> = {
  direction: 'Direction',
  finance: 'Finance',
  logistics: 'Logistique',
  support: 'Support',
};

const ACQ_COLORS = [
  'var(--a-forest)',
  'var(--a-terracotta)',
  'var(--a-ochre)',
  'var(--a-info)',
  'var(--a-ink-mute)',
];
const SRC_LABEL: Record<string, string> = {
  organic: 'SEO / organique',
  csr: 'Création CSR',
  referral: 'Parrainage',
  paid_meta: 'Publicité Meta',
  paid_google: 'Publicité Google',
  import: 'Import',
  other: 'Autre',
};

const TICKET_STATUS_LABEL: Record<string, string> = {
  open: 'Ouverts',
  pending: 'En attente',
  resolved: 'Résolus',
  closed: 'Fermés',
};

const TICKET_PRIORITY_LABEL: Record<string, string> = {
  urgent: 'Urgent',
  high: 'Haut',
  normal: 'Normal',
  low: 'Bas',
};

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace('.', ',')}k`;
  return String(Math.round(n));
}

function pct(value: number, total: number): number {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

function dateLabel(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function EmptyState({ children = 'Aucune donnée.' }: { children?: string }) {
  return <div className="empty">{children}</div>;
}

function LoadingState() {
  return <div className="empty">Chargement…</div>;
}

type QueryResult<T> = UseQueryResult<T, Error>;

export function AdminReportingPage() {
  const { has, isSuperAdmin } = usePermissions();
  const [view, setView] = useState<ViewKey>('direction');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const canDirection = isSuperAdmin || has(VIEW_PERMISSIONS.direction);
  const canFinance = isSuperAdmin || has(VIEW_PERMISSIONS.finance);
  const canLogistics = isSuperAdmin || has(VIEW_PERMISSIONS.logistics);
  const canSupport = isSuperAdmin || has(VIEW_PERMISSIONS.support);
  const canExport = isSuperAdmin || has('FINANCE.EXPORT') || has('FINANCE.EXPORT.ACCOUNTING');

  const options = useMemo(
    () =>
      [
        canDirection && { value: 'direction' as const, label: VIEW_LABEL.direction },
        canFinance && { value: 'finance' as const, label: VIEW_LABEL.finance },
        canLogistics && { value: 'logistics' as const, label: VIEW_LABEL.logistics },
        canSupport && { value: 'support' as const, label: VIEW_LABEL.support },
      ].filter(Boolean) as Array<{ value: ViewKey; label: string }>,
    [canDirection, canFinance, canLogistics, canSupport],
  );

  useEffect(() => {
    if (options.length > 0 && !options.some((o) => o.value === view)) {
      setView(options[0].value);
    }
  }, [options, view]);

  // Une requête par vue, sur l'endpoint cloisonné correspondant (cloisonnement
  // serveur effectif) : plus aucune recomposition depuis les APIs de module.
  const directionQuery = useQuery({
    queryKey: ['reporting', 'business', 'direction'],
    queryFn: () => reportingBusinessApi.get('direction'),
    enabled: canDirection && view === 'direction',
  });
  const financeQuery = useQuery({
    queryKey: ['reporting', 'business', 'finance'],
    queryFn: () => reportingBusinessApi.get('finance'),
    enabled: canFinance && view === 'finance',
  });
  const logisticsQuery = useQuery({
    queryKey: ['reporting', 'business', 'logistics'],
    queryFn: () => reportingBusinessApi.get('logistics'),
    enabled: canLogistics && view === 'logistics',
  });
  const supportQuery = useQuery({
    queryKey: ['reporting', 'business', 'support'],
    queryFn: () => reportingBusinessApi.get('support'),
    enabled: canSupport && view === 'support',
  });

  const exportMonthlySummary = async () => {
    // Dernier mois clos (mois précédent), pas le mois en cours incomplet (4.4).
    const now = new Date();
    const lastClosed = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setIsExporting(true);
    setExportError(null);
    try {
      await monthlySummaryApi.downloadCsv(lastClosed.getFullYear(), lastClosed.getMonth() + 1);
    } catch {
      setExportError("La synthèse mensuelle n'a pas pu être générée.");
    } finally {
      setIsExporting(false);
    }
  };

  if (options.length === 0) {
    return (
      <>
        <PageHead title="Reporting" sub="Tableaux de bord métier" />
        <Card>
          <CardBody>
            <EmptyState>Aucun tableau de bord autorisé pour ce compte.</EmptyState>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHead
        title="Reporting"
        sub="Tableaux de bord par périmètre métier"
        actions={
          <AdminButton
            variant="ghost"
            onClick={exportMonthlySummary}
            disabled={!canExport || isExporting}
            title={canExport ? undefined : 'Permission export requise'}
          >
            <AdminIcon n="download" s={14} />
            {isExporting ? 'Génération…' : 'Synthèse mensuelle'}
          </AdminButton>
        }
      />
      {exportError && (
        <div className="empty" style={{ marginBottom: 12 }}>
          {exportError}
        </div>
      )}

      <Segmented<ViewKey> value={view} onChange={setView} options={options} />

      <div style={{ marginTop: 16 }}>
        {view === 'direction' && <DirectionView query={directionQuery} />}
        {view === 'finance' && <FinanceView query={financeQuery} />}
        {view === 'logistics' && <LogisticsView query={logisticsQuery} />}
        {view === 'support' && <SupportView query={supportQuery} />}
      </div>
    </>
  );
}

function DirectionView({ query }: { query: QueryResult<DirectionReport> }) {
  const data = query.data;
  const loading = query.isLoading;

  const sales = data?.sales;
  const funnel = data?.funnel;
  const today = data?.today;
  const finance = data?.finance;
  const logistics = data?.logistics;
  const support = data?.support;
  const series = data?.revenueSeries ?? [];

  const revenueData = series.map((d) => d.revenue);
  const dayLabels = series.map((d) =>
    new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
  );
  const maxRev = Math.max(1, ...revenueData);

  const acqSources = data?.customer.acquisition.bySource ?? [];
  const acqTotal =
    data?.customer.acquisition.total || acqSources.reduce((s, x) => s + x.count, 0) || 1;
  const catalogBars = [
    data?.catalog.published ?? 0,
    data?.catalog.draft ?? 0,
    data?.catalog.paused ?? 0,
    data?.catalog.archived ?? 0,
  ];

  return (
    <>
      <div className="kpi-grid">
        <KpiBox
          label="CA commandes"
          value={loading ? '…' : fmtFCFA(sales?.revenue ?? 0)}
          variant="accent"
          foot={`Aujourd'hui : ${fmtFCFA(today?.revenue ?? 0)}`}
        />
        <KpiBox
          label="Commandes"
          value={loading ? '…' : (sales?.orders ?? 0)}
          variant="forest"
          foot={`Aujourd'hui : ${today?.orders ?? 0}`}
        />
        <KpiBox
          label="Panier moyen"
          value={loading ? '…' : fmtFCFA(sales?.averageBasket ?? 0)}
          foot="CA / commandes de la période"
        />
        <KpiBox
          label="Commandes abouties"
          value={loading ? '…' : `${funnel?.orderCompletionRate ?? 0}%`}
          variant="success"
          foot={`${today?.slaLate ?? 0} SLA en retard`}
        />
      </div>

      <div className="grid-2">
        <Card>
          <CardHead title="CA par jour" sub="Revenu des commandes sur la période" />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : revenueData.length === 0 ? (
              <EmptyState />
            ) : (
              <AdminLineChart
                data={revenueData}
                labels={dayLabels}
                yLabels={['0', compact(maxRev / 2), compact(maxRev)]}
                color="var(--a-terracotta)"
                height={240}
              />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHead title="Finance" sub="Trésorerie et exposition court terme" />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : (
              <>
                <InfoRow label="Trésorerie" value={fmtFCFA(finance?.treasury ?? 0)} />
                <InfoRow label="Créances" value={fmtFCFA(finance?.receivables ?? 0)} />
                <InfoRow label="Dettes fournisseurs" value={fmtFCFA(finance?.payables ?? 0)} />
                <InfoRow
                  label="TVA collectée (mois)"
                  value={fmtFCFA(finance?.vatCollectedMonth ?? 0)}
                />
              </>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="grid-3" style={{ marginTop: 16 }}>
        <Card>
          <CardHead
            title="Acquisition clients"
            sub={`${data?.customer.acquisition.total ?? 0} clients`}
          />
          <CardBody>
            {loading ? <LoadingState /> : acqSources.length === 0 && <EmptyState />}
            {acqSources.map((s, i) => {
              const share = pct(s.count, acqTotal);
              return (
                <div key={s.source} style={{ padding: '7px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12 }}>{SRC_LABEL[s.source] ?? s.source}</span>
                    <strong className="tn">{share}%</strong>
                  </div>
                  <div className="bar thin">
                    <i
                      style={{ width: `${share}%`, background: ACQ_COLORS[i % ACQ_COLORS.length] }}
                    />
                  </div>
                </div>
              );
            })}
          </CardBody>
        </Card>

        <Card>
          <CardHead title="Logistique" sub="Performance livraison" />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : (
              <>
                <InfoRow label="Colis traités" value={logistics?.shipments.total ?? 0} />
                <InfoRow label="Colis livrés" value={logistics?.shipments.delivered ?? 0} />
                <InfoRow label="SLA réussi" value={`${logistics?.slaSuccessRate ?? 0}%`} />
                <InfoRow
                  label="Premier passage"
                  value={`${logistics?.firstAttemptSuccessRate ?? 0}%`}
                />
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHead title="Support" sub="Charge et risque SLA" />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : (
              <>
                <InfoRow label="Ouverts" value={support?.open ?? 0} />
                <InfoRow label="Urgents" value={support?.urgent ?? 0} />
                <InfoRow label="Non assignés" value={support?.unassigned ?? 0} />
                <InfoRow label="SLA dépassé" value={support?.slaBreached ?? 0} />
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHead title="Catalogue" sub="Disponibilité commerciale" />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : (
              <AdminBarChart
                data={catalogBars}
                labels={['Publiés', 'Brouillons', 'En pause', 'Archivés']}
                color="var(--a-terracotta)"
                height={130}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function FinanceView({ query }: { query: QueryResult<FinanceReport> }) {
  const loading = query.isLoading;
  const finance = query.data?.finance;
  const accounts = finance?.treasuryByAccount ?? [];
  const alerts = finance?.alerts ?? [];

  return (
    <>
      <div className="kpi-grid">
        <KpiBox
          label="Trésorerie"
          value={loading ? '…' : fmtFCFA(finance?.treasury ?? 0)}
          variant="accent"
          foot={`${accounts.length} comptes`}
        />
        <KpiBox
          label="Créances clients"
          value={loading ? '…' : fmtFCFA(finance?.receivables ?? 0)}
          variant="forest"
        />
        <KpiBox
          label="Échues"
          value={loading ? '…' : fmtFCFA(finance?.overdueAmount ?? 0)}
          variant="warning"
          foot={`${finance?.overdueCount ?? 0} factures`}
        />
        <KpiBox
          label="TVA collectée"
          value={loading ? '…' : fmtFCFA(finance?.vatCollectedMonth ?? 0)}
          foot="Mois en cours"
        />
      </div>

      <div className="grid-2">
        <Card>
          <CardHead title="Trésorerie par compte" sub="Soldes courants" />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : accounts.length === 0 ? (
              <EmptyState>Aucun mouvement de trésorerie.</EmptyState>
            ) : (
              <div className="table-wrap" style={{ margin: 0 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Compte</th>
                      <th>Libellé</th>
                      <th className="right">Solde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((a) => (
                      <tr key={a.accountCode}>
                        <td className="mono">{a.accountCode}</td>
                        <td>{a.label}</td>
                        <td className="right tn">
                          <strong>{fmtFCFA(a.balance)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHead title="Alertes finance" sub="Contrôles et recouvrement" />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : (
              alerts.length === 0 && <EmptyState>Aucune alerte finance.</EmptyState>
            )}
            {alerts.map((a, i) => (
              <div key={i} className="feed-item">
                <span
                  className="dot"
                  style={{
                    background:
                      a.level === 'danger'
                        ? 'var(--a-danger)'
                        : a.level === 'warn'
                          ? 'var(--a-warning)'
                          : 'var(--a-info)',
                  }}
                />
                <div className="body">{a.message}</div>
              </div>
            ))}
            {!loading && (
              <div style={{ marginTop: 12 }}>
                <InfoRow label="Dettes fournisseurs" value={fmtFCFA(finance?.payables ?? 0)} />
                <InfoRow label="Factures en retard" value={finance?.overdueCount ?? 0} />
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function LogisticsView({ query }: { query: QueryResult<LogisticsReport> }) {
  const loading = query.isLoading;
  const kpi = query.data?.logistics;
  const shipments = query.data?.lateShipments.items ?? [];
  const lateTotal = query.data?.lateShipments.total ?? 0;
  const tours = query.data?.tours ?? [];

  return (
    <>
      <div className="kpi-grid">
        <KpiBox
          label="Colis"
          value={loading ? '…' : (kpi?.shipments.total ?? 0)}
          variant="accent"
          foot={`${kpi?.shipments.delivered ?? 0} livrés`}
        />
        <KpiBox
          label="Taux livraison"
          value={loading ? '…' : `${kpi?.deliveryRate ?? 0}%`}
          variant="forest"
        />
        <KpiBox
          label="SLA réussi"
          value={loading ? '…' : `${kpi?.slaSuccessRate ?? 0}%`}
          variant="success"
        />
        <KpiBox label="Tournées actives" value={loading ? '…' : tours.length} foot="En cours" />
      </div>

      <div className="grid-2">
        <Card>
          <CardHead title="Colis en retard SLA" sub={`${lateTotal} colis concernés`} />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : shipments.length === 0 ? (
              <EmptyState>Aucun retard SLA.</EmptyState>
            ) : (
              <div className="table-wrap" style={{ margin: 0 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Colis</th>
                      <th>Statut</th>
                      <th>COD</th>
                      <th>SLA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipments.map((s) => (
                      <tr key={s.id}>
                        <td className="mono">{s.code}</td>
                        <td>
                          <Tag variant="warning">{s.status}</Tag>
                        </td>
                        <td>{s.codAmount ? fmtFCFA(s.codAmount) : '—'}</td>
                        <td className="tn">{dateLabel(s.slaDueAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHead title="Tournées en cours" sub="Suivi opérationnel" />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : (
              tours.length === 0 && <EmptyState>Aucune tournée en cours.</EmptyState>
            )}
            {tours.map((t) => (
              <div key={t.id} className="feed-item">
                <span className="dot" style={{ background: 'var(--a-forest)' }} />
                <div className="body">
                  <strong>{t.code}</strong>
                  <div className="muted">
                    {t.driverName ?? 'Chauffeur non assigné'} · {t.totalStops} arrêts
                  </div>
                </div>
                <span className="ts tn">{t.plate ?? '—'}</span>
              </div>
            ))}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function SupportView({ query }: { query: QueryResult<SupportReport> }) {
  const loading = query.isLoading;
  const stats = query.data?.support;
  const items = query.data?.urgentTickets.items ?? [];
  const urgentTotal = query.data?.urgentTickets.total ?? 0;

  return (
    <>
      <div className="kpi-grid">
        <KpiBox label="Ouverts" value={loading ? '…' : (stats?.open ?? 0)} variant="accent" />
        <KpiBox label="Urgents" value={loading ? '…' : (stats?.urgent ?? 0)} variant="warning" />
        <KpiBox label="Non assignés" value={loading ? '…' : (stats?.unassigned ?? 0)} />
        <KpiBox
          label="SLA dépassé"
          value={loading ? '…' : (stats?.slaBreached ?? 0)}
          variant="forest"
        />
      </div>

      <div className="grid-2">
        <Card>
          <CardHead title="File urgente" sub={`${urgentTotal} tickets urgents`} />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : items.length === 0 ? (
              <EmptyState>Aucun ticket urgent.</EmptyState>
            ) : (
              <div className="table-wrap" style={{ margin: 0 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>Sujet</th>
                      <th>Priorité</th>
                      <th>Statut</th>
                      <th>SLA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((t) => (
                      <tr key={t.id}>
                        <td className="mono">{t.number}</td>
                        <td>
                          <strong style={{ fontSize: 12 }}>{t.subject}</strong>
                        </td>
                        <td>
                          <Tag variant={t.priority === 'urgent' ? 'danger' : 'warning'}>
                            {TICKET_PRIORITY_LABEL[t.priority] ?? t.priority}
                          </Tag>
                        </td>
                        <td>{TICKET_STATUS_LABEL[t.status] ?? t.status}</td>
                        <td className="tn">{dateLabel(t.slaDueAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHead title="Répartition support" sub="Charge par statut" />
          <CardBody>
            {loading ? (
              <LoadingState />
            ) : (
              <>
                <InfoRow label="Ouverts" value={stats?.open ?? 0} />
                <InfoRow label="En attente" value={stats?.pending ?? 0} />
                <InfoRow label="Résolus" value={stats?.resolved ?? 0} />
                <InfoRow label="Total" value={stats?.total ?? 0} />
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function KpiBox({
  label,
  value,
  foot,
  variant,
}: {
  label: string;
  value: string | number;
  foot?: string | number;
  variant?: 'accent' | 'forest' | 'success' | 'warning';
}) {
  return (
    <div className={`kpi ${variant ?? ''}`}>
      <div className="kpi-l">{label}</div>
      <div className="kpi-v tn">{value}</div>
      {foot !== undefined && <div className="kpi-foot muted">{foot}</div>}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="info-row">
      <span className="l">{label}</span>
      <span className="v tn">{value}</span>
    </div>
  );
}
